import { BRAND_CODE_MAP } from './brands';
import { PROGRAM_CODE_MAP, resolveProgramName } from './dataNormalizer';

export const PROGRAM_CATEGORY_MAP = {
  Website: 'DLRWST',
  Chat: 'CHAT',
  Trade: 'TRTL',
  SEO: 'SEO',
  'Digital Ad Sales': 'DGA',
  'Digital Ad Service': 'SDGA',
};

const CATEGORY_CODES = new Set(Object.values(PROGRAM_CATEGORY_MAP));
const VMT_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{2,4}$/;
const BRAND_NAME_TO_CODE = Object.fromEntries(
  Object.entries(BRAND_CODE_MAP).map(([code, name]) => [name.toUpperCase(), code])
);

export const CATEGORY_PROGRAM_MAP = Object.fromEntries(
  Object.entries(PROGRAM_CATEGORY_MAP).map(([program, category]) => [category, program])
);

export const pickString = (values = []) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
      continue;
    }
    const stringified = String(value).trim();
    if (stringified.length > 0) return stringified;
  }
  return '';
};

export const normalizeAccutradeCoreProductCode = (value) => {
  if (value === null || value === undefined) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return trimmed;
  return trimmed.replace(/^AT_AT[CS](?=$|_)/i, 'AT_ACS');
};

export const canonicalizeBrandToken = (value) => {
  if (value === null || value === undefined) return '';
  const token = String(value).trim();
  if (!token) return '';
  const upper = token.toUpperCase();
  if (upper === 'K' || upper === 'CAD' || upper === 'CADILLAC') return 'CAD';
  if (upper === 'C' || upper === 'B' || upper === 'G') return upper;
  const mapped = BRAND_NAME_TO_CODE[upper];
  if (mapped) {
    return mapped === 'K' ? 'CAD' : mapped;
  }
  return '';
};

export const normalizeBrandCode = (value) => {
  if (!value && value !== 0) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';

  const normalizedSingle = canonicalizeBrandToken(trimmed);
  if (normalizedSingle) return normalizedSingle;

  const upper = trimmed.toUpperCase();
  const tokens = [];
  let index = 0;
  while (index < upper.length) {
    if (upper.startsWith('CAD', index)) {
      tokens.push('CAD');
      index += 3;
      continue;
    }
    const char = upper[index];
    if (char === ',' || char === '/' || char === '|' || char === '-') {
      index += 1;
      continue;
    }
    if (char === ' ') {
      index += 1;
      continue;
    }
    if (char === 'K') {
      tokens.push('CAD');
      index += 1;
      continue;
    }
    if (char === 'C' || char === 'B' || char === 'G') {
      tokens.push(char);
      index += 1;
      continue;
    }
    return '';
  }

  if (tokens.length === 0) return '';
  const deduped = [];
  tokens.forEach((token) => {
    const canonical = canonicalizeBrandToken(token);
    if (canonical && !deduped.includes(canonical)) {
      deduped.push(canonical);
    }
  });
  return deduped.join('');
};

const BRAND_ENTRY_KEYS = ['brandCd', 'brandCode', 'code', 'gmBrandCd', 'gmBrandCode', 'value'];

export const coerceBrandCodeCandidate = (value) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    const raw = String(value).trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (BRAND_NAME_TO_CODE[upper]) return BRAND_NAME_TO_CODE[upper];
    if (upper.length === 1) return upper;
    if (BRAND_CODE_MAP[upper]) return upper;
    return raw;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const candidate = coerceBrandCodeCandidate(entry);
      if (candidate) return candidate;
    }
    return '';
  }

  if (typeof value === 'object') {
    for (const key of BRAND_ENTRY_KEYS) {
      if (value[key]) {
        const candidate = coerceBrandCodeCandidate(value[key]);
        if (candidate) return candidate;
      }
    }
  }

  return '';
};

const PROGRAM_NAME_CANDIDATE_KEYS = ['programName', 'programLabel', 'label', 'name', 'value', 'text', 'title'];
const PROGRAM_NAME_CANONICAL_MAP = new Map(
  Object.keys(PROGRAM_CODE_MAP).map((name) => [name.toLowerCase(), name])
);

export const normalizeProgramNameValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeProgramNameValue(item);
      if (normalized) return normalized;
    }
    return '';
  }
  if (typeof value === 'object' && value !== null) {
    for (const key of PROGRAM_NAME_CANDIDATE_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      const normalized = normalizeProgramNameValue(value[key]);
      if (normalized) return normalized;
    }
    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return stringified.trim();
      }
    }
  }
  return '';
};

export const getProgramNameFromRecord = (record = {}) => {
  const candidates = [
    record.programName,
    record.programLabel,
    record.program,
    record.categoryName,
    record.categoryCd,
    record.programCode,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeProgramNameValue(candidate);
    if (normalized) return normalized;
  }
  return '';
};

const STATUS_LABEL_MAP = {
  LIVE: 'Live',
  ACTIVE: 'Live',
  PENDING_LIVE: 'Pending Live',
  PENDING_GO_LIVE: 'Pending Live',
  PENDING_CANCEL: 'Pending Cancel',
  PENDING_CANCELLATION: 'Pending Cancel',
  PENDING_CANC: 'Pending Cancel',
  CANCELLED: 'Cancelled',
  CANCELED: 'Cancelled',
};

const STATUS_LABEL_SET = new Set(Object.values(STATUS_LABEL_MAP));

export const normalizeLifecycleStatus = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  if (STATUS_LABEL_SET.has(stringValue)) return stringValue;
  const key = stringValue.replace(/[\s-]+/g, '_').toUpperCase();
  if (STATUS_LABEL_MAP[key]) return STATUS_LABEL_MAP[key];
  return '';
};

const DESIGNATION_LABEL_MAP = {
  PRODUCT: 'Product',
  PACKAGE: 'Package',
  A_LA_CARTE: 'A la carte',
  A_LA_CART: 'A la carte',
  MONTHLY_FEE: 'Monthly Fee',
  ONE_TIME_FEE: 'One Time Fee',
  MANAGEMENT_FEE: 'Management Fee',
  SETUP_FEE: 'Setup Fee',
  OTHER_FEE: 'Other Fee',
  SALES_TAX: 'Sales Tax',
};

export const normalizeDesignationLabel = (value, fallback = 'Package') => {
  if (value === null || value === undefined) return fallback;
  const trimmed = String(value).trim();
  if (!trimmed) return fallback;
  const normalizedKey = trimmed.replace(/[^\w]+/g, '_').toUpperCase();
  if (DESIGNATION_LABEL_MAP[normalizedKey]) {
    return DESIGNATION_LABEL_MAP[normalizedKey];
  }
  if (trimmed.toLowerCase() === 'a la carte' || trimmed.toLowerCase() === 'à la carte') {
    return 'A la carte';
  }
  return trimmed;
};

export const normalizeImrUsage = (value) => {
  if (value === null || value === undefined || value === '') return 'Y';
  if (typeof value === 'boolean') return value ? 'Y' : 'N';
  const trimmed = String(value).trim().toUpperCase();
  if (trimmed === 'TRUE' || trimmed === 'YES') return 'Y';
  if (trimmed === 'FALSE' || trimmed === 'NO') return 'N';
  if (trimmed === 'Y' || trimmed === 'N') return trimmed;
  return 'Y';
};

const normalizeServiceMonth = (value) => {
  if (value === null || value === undefined) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 12) {
    return String(numeric);
  }
  return '';
};

const formatUtcDateParts = (parsed) => {
  const month = `${parsed.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getUTCDate()}`.padStart(2, '0');
  const year = `${parsed.getUTCFullYear()}`.slice(-2);
  return `${month}/${day}/${year}`;
};

const parseVmtDateString = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!VMT_DATE_PATTERN.test(trimmed)) return null;
  const [monthToken, dayToken, yearToken] = trimmed.split('/');
  const month = Number.parseInt(monthToken, 10);
  const day = Number.parseInt(dayToken, 10);
  const rawYear = Number.parseInt(yearToken, 10);
  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(rawYear)) {
    return null;
  }
  const fullYear = yearToken.length === 2 ? 2000 + rawYear : rawYear;
  const parsed = new Date(Date.UTC(fullYear, month - 1, day));
  const isValid =
    parsed.getUTCFullYear() === fullYear &&
    parsed.getUTCMonth() + 1 === month &&
    parsed.getUTCDate() === day;
  if (!isValid) return null;
  return { month, day, fullYear };
};

const isValidVmtDateString = (value) => {
  return Boolean(parseVmtDateString(value));
};

const extractMonthFromDate = (value) => {
  const normalized = formatDateForVmt(value);
  if (!VMT_DATE_PATTERN.test(normalized)) return '';
  return String(Number.parseInt(normalized.slice(0, 2), 10));
};

export const deriveServiceMonth = (selection = {}, context = {}) => {
  const direct = normalizeServiceMonth(
    pickString([
      selection.serviceMonth,
      selection.serviceMonthNumber,
      selection.serviceMonthNumeric,
      selection.serviceMonthValue,
      selection.monthOfService,
      context.serviceMonth,
      context.serviceMonthNumber,
    ])
  );
  if (direct) return direct;
  const fromDate =
    extractMonthFromDate(selection.billingDate || selection.billingStartDate) ||
    extractMonthFromDate(selection.serviceMonthDate) ||
    extractMonthFromDate(selection.selectionDate || selection.productSelectionDate) ||
    extractMonthFromDate(selection.effectiveDate || selection.goLiveDate) ||
    '';
  return normalizeServiceMonth(fromDate);
};

const REQUIRED_FIELDS = [
  'selectionDate',
  'effectiveDate',
  'bac',
  'countryCd',
  'categoryCd',
  'vendorCd',
  'gmBrandCd',
  'productCd',
  'productDescription',
  'designation',
  'itemQuantity',
  'status',
  'lastUpdatedDate',
  'lastUpdatedBy',
];

const DATE_FIELDS = ['billingDate', 'selectionDate', 'effectiveDate', 'lastUpdatedDate'];

export const formatDateForVmt = (value) => {
  if (!value) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatUtcDateParts(new Date(value));
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return formatUtcDateParts(value);
  }
  if (value && typeof value === 'object' && typeof value.isValid === 'function' && value.isValid()) {
    if (typeof value.toDate === 'function') {
      return formatDateForVmt(value.toDate());
    }
  }
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (VMT_DATE_PATTERN.test(trimmed)) {
    const parsedSlashDate = parseVmtDateString(trimmed);
    if (!parsedSlashDate) return trimmed;
    return formatUtcDateParts(new Date(Date.UTC(parsedSlashDate.fullYear, parsedSlashDate.month - 1, parsedSlashDate.day)));
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return formatUtcDateParts(parsed);
};

export const sanitizeRecord = (record = {}) => {
  const next = {};
  Object.entries(record).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      next[key] = ['promoCd', 'futureUse1', 'futureUse2', 'futureUse3'].includes(key) ? null : '';
      return;
    }
    if (DATE_FIELDS.includes(key)) {
      next[key] = formatDateForVmt(value);
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      next[key] = key === 'productCd' ? normalizeAccutradeCoreProductCode(trimmed) : trimmed;
      return;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      next[key] = String(value);
      return;
    }
    next[key] = value;
  });
  return next;
};

export const validateVmtRecord = (record) => {
  const errors = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (!record[field] || (typeof record[field] === 'string' && record[field].trim().length === 0)) {
      errors[field] = 'Required';
    }
  });
  DATE_FIELDS.forEach((field) => {
    if (!record[field]) return;
    if (!isValidVmtDateString(record[field])) {
      errors[field] = 'Must be a valid MM/DD/YY date';
    }
  });
  if (record.bac && !/^\d{6}$/.test(String(record.bac).trim())) {
    errors.bac = 'BAC must be a 6-digit string';
  }
  if (record.serviceMonth && !/^(0?[1-9]|1[0-2])$/.test(String(record.serviceMonth).trim())) {
    errors.serviceMonth = 'Service month must be between 1 and 12';
  }
  if (record.imrUsage && !['Y', 'N'].includes(String(record.imrUsage).trim().toUpperCase())) {
    errors.imrUsage = 'Must be Y or N';
  }
  if (record.imrPercentage && !/^([0-9]{1,2}(\.\d+)?|100)$/.test(String(record.imrPercentage).trim())) {
    errors.imrPercentage = '0-100 expected';
  }

  if (record.programName) {
    const normalizedProgramName = normalizeProgramNameValue(record.programName);
    if (normalizedProgramName) {
      const resolved = resolveProgramName(normalizedProgramName);
      if (!resolved) {
        const key = normalizedProgramName.toLowerCase();
        if (!PROGRAM_NAME_CANONICAL_MAP.has(key)) {
          errors.programName = 'Program name is not recognized';
        }
      }
    }
  }
  return errors;
};

export const resolveCategoryCd = (selection = {}, context = {}) => {
  const codeCandidates = [
    selection.categoryCd,
    selection.categoryCode,
    selection.programCategoryCd,
    selection.programCategoryCode,
    context.categoryCd,
    context.categoryCode,
  ];
  for (const candidate of codeCandidates) {
    const trimmed = pickString([candidate]);
    if (!trimmed) continue;
    const upper = trimmed.toUpperCase();
    if (CATEGORY_CODES.has(upper)) return upper;
  }

  const programCandidates = [
    selection.programName,
    selection.programLabel,
    selection.categoryName,
    context.programName,
    context.programLabel,
    context.categoryName,
    context.categoryCd,
  ];

  for (const candidate of programCandidates) {
    if (!candidate) continue;
    const normalized = PROGRAM_CATEGORY_MAP[candidate];
    if (normalized) return normalized;
  }

  return '';
};
